import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import FinancialAccountService from '../../services/FinancialAccountService';

export default function FinancialAccountDetailPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await FinancialAccountService.getDetail(id);
      if (res.success) {
        setData(res.data);
      } else {
        toast.current.show({ severity: 'error', summary: 'Hata', detail: res.message });
      }
    } catch (err) {
      toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Hesap detayları yüklenemedi' });
    } finally {
      setLoading(false);
    }
  };

  const getAccountTypeLabel = (type) => {
    switch (type) {
      case 1: return 'Kasa Hesabı';
      case 2: return 'Banka Hesabı';
      case 3: return 'Kredi Kartı';
      default: return 'Diğer';
    }
  };

  const amountBodyTemplate = (rowData) => {
    const isNegative = rowData.transactionType === 'OUTBOUND';
    return (
      <span className={`font-black ${isNegative ? 'text-red-600' : 'text-emerald-700'}`}>
        {isNegative ? '-' : '+'}{rowData.amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
      </span>
    );
  };

  const dateBodyTemplate = (rowData) => {
    return (
      <div className="flex flex-col">
        <span className="font-bold text-slate-700">{new Date(rowData.transactionDate).toLocaleDateString('tr-TR')}</span>
        <span className="text-[10px] text-slate-400 font-medium">{new Date(rowData.transactionDate).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
      </div>
    );
  };

  const referenceTemplate = (rowData) => {
    if (!rowData.referenceType) return <span className="text-slate-400">-</span>;
    return (
      <Tag
        value={rowData.referenceType}
        className="text-[9px] font-black uppercase px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200"
      />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="pi pi-spin pi-spinner text-4xl text-slate-300"></i>
      </div>
    );
  }

  const inboundTransactions = data?.transactions.filter(t => t.transactionType === 'INBOUND') || [];
  const outboundTransactions = data?.transactions.filter(t => t.transactionType === 'OUTBOUND') || [];

  return (
    <div className="p-4 md:p-6 space-y-6 animate-fade-in">
      <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            icon="pi pi-arrow-left"
            className="p-button-text p-button-secondary p-button-rounded"
            onClick={() => navigate('/finance/accounts')}
          />
          <div>
            <h1 className="text-2xl font-black text-slate-800 m-0 uppercase tracking-tight">
              {data?.account.name}
            </h1>
            <p className="text-sm text-slate-500 font-medium">
              {getAccountTypeLabel(data?.account.accountType)} • {data?.account.accountNumber || 'Hesap No Belirtilmemiş'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            label="Düzenle"
            icon="pi pi-pencil"
            className="p-button-outlined p-button-secondary rounded-xl font-bold"
            onClick={() => navigate(`/finance/accounts/edit/${id}`)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Summary Card */}
        <Card className="lg:col-span-1 shadow-xl border border-slate-200 rounded-[2rem] overflow-hidden">
          <div className="space-y-6">
            <div>
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">TOPLAM BAKİYE</span>
              <div className="flex items-baseline gap-2">
                <span className={`text-4xl font-black ${data?.account.balance >= 0 ? 'text-slate-800' : 'text-red-600'}`}>
                  {data?.account.balance?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </span>
                <span className="text-lg font-bold text-slate-400">{data?.account.currencyCode}</span>
              </div>
            </div>

            <div className="space-y-4 pt-6 border-t border-slate-50">
              <div className="flex justify-between">
                <span className="text-xs font-bold text-slate-500">Açılış Bakiyesi</span>
                <span className="text-xs font-black text-slate-700">{data?.account.openingBalance?.toLocaleString()} ₺</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs font-bold text-slate-500">Hesap Tipi</span>
                <span className="text-xs font-black text-slate-700">{getAccountTypeLabel(data?.account.accountType)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs font-bold text-slate-500">Durum</span>
                <Tag
                  value={data?.account.isActive ? 'AKTİF' : 'PASİF'}
                  severity={data?.account.isActive ? 'success' : 'danger'}
                  className="text-[9px] font-black"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Transactions Tabs / Tables */}
        <div className="lg:col-span-3 space-y-6">
          <div className="bg-white shadow-xl border border-slate-200 rounded-[2rem] overflow-hidden">
            <div className="p-6 border-b border-slate-50 flex items-center justify-between">
                <h2 className="text-sm font-black text-slate-800 m-0 uppercase tracking-widest">GİRİŞ HAREKETLERİ (TAHSİLATLAR)</h2>
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                    <i className="pi pi-arrow-down-left text-xs"></i>
                </div>
            </div>
            <DataTable
              value={inboundTransactions}
              paginator
              rows={5}
              className="p-datatable-sm"
              emptyMessage="Giriş hareketi bulunamadı"
            >
              <Column header="TARİH" body={dateBodyTemplate} className="w-32" />
              <Column field="description" header="AÇIKLAMA" className="text-sm font-medium" />
              <Column header="REFERANS" body={referenceTemplate} className="w-32" />
              <Column header="TUTAR" body={amountBodyTemplate} className="text-right w-32" />
            </DataTable>
          </div>

          <div className="bg-white shadow-xl border border-slate-200 rounded-[2rem] overflow-hidden">
            <div className="p-6 border-b border-slate-50 flex items-center justify-between">
                <h2 className="text-sm font-black text-slate-800 m-0 uppercase tracking-widest">ÇIKIŞ HAREKETLERİ (ÖDEMELER)</h2>
                <div className="w-8 h-8 rounded-lg bg-red-50 text-red-600 flex items-center justify-center">
                    <i className="pi pi-arrow-up-right text-xs"></i>
                </div>
            </div>
            <DataTable
              value={outboundTransactions}
              paginator
              rows={5}
              className="p-datatable-sm"
              emptyMessage="Çıkış hareketi bulunamadı"
            >
              <Column header="TARİH" body={dateBodyTemplate} className="w-32" />
              <Column field="description" header="AÇIKLAMA" className="text-sm font-medium" />
              <Column header="REFERANS" body={referenceTemplate} className="w-32" />
              <Column header="TUTAR" body={amountBodyTemplate} className="text-right w-32" />
            </DataTable>
          </div>
        </div>
      </div>
    </div>
  );
}
